import { Checkbox, Grid, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import Loading from 'components/Loading';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import i18n from 'i18n';
import PermissionCard from 'containers/Groups/PermissionCard';
import Alert from '@material-ui/lab/Alert';
import styles from '../../Template/styles';
import { ENTITY_NAME } from './Content';

const useStyles = makeStyles(styles);

interface Props {
  title: string;
  subtitle: string;
  isLoading: boolean;
  permissions?: {
    loading: boolean;
    selected: string[];
    available: { name: string; selected: boolean }[];
  };
  pending: string[];
  setPending: (pending: string[]) => void;
  updatePermissions: (permissions: string[]) => void;
}

const ListPermissions = ({
  title,
  subtitle,
  permissions,
  pending,
  isLoading,
  setPending,
  updatePermissions,
}: Props) => {
  const classes = useStyles();
  const [checked, setChecked] = React.useState<string[]>([]);

  const permissionsNames: string[] = permissions?.available?.map(
    item => item.name
  ) || [''];

  const permissionList = [
    {
      key: 'general',
      label: i18n.t('GENERAL_KEY'),
      values: ['can_view_all_resources'].filter(item =>
        permissionsNames.includes(item)
      ),
    },
    {
      key: 'operator',
      label: i18n.t('OPERATORS_KEY'),
      values: [
        'list_all_operators',
        'list_self_operator',
        'list_operator_details',
        'list_self_operator_details',
        'create_new_operator',
        'edit_self_operator_details',
        'edit_operator_details',
        'delete_operator',
        'delete_self_operator',
        'edit_operator_role',
        'list_all_operators_audit_logs',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'token',
      label: 'Token',
      values: [
        'list_all_operator_token',
        'list_self_operator_token',
        'create_self_api_token',
        'create_api_token',
        'edit_self_api_token',
        'edit_api_token',
        'delete_self_api_token',
        'delete_api_token',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'permission-role',
      label: i18n.t('PERMISSION_ROLES_KEY'),
      values: [
        'list_all_roles',
        'create_all_roles',
        'edit_all_roles',
        'delete_all_roles',
        'can_clone_role',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'mfa',
      label: 'MFA',
      values: ['reset_operator_mfa', 'reset_self_operator_mfa'].filter(item =>
        permissionsNames.includes(item)
      ),
    },
    {
      key: 'company',
      label: i18n.t('COMPANY_KEY'),
      values: ['create_new_company'].filter(item =>
        permissionsNames.includes(item)
      ),
    },
    {
      key: 'user',
      label: i18n.t('USERS_KEY'),
      values: ['list_all_users', 'can_export_user_summary_list'].filter(item =>
        permissionsNames.includes(item)
      ),
    },
    {
      key: 'monitor',
      label: i18n.t('GROUPS_PAGE.MONITORS_KEY'),
      values: ['list_all_monitors'].filter(item =>
        permissionsNames.includes(item)
      ),
    },
  ].filter(item => item.values.length > 0);

  const handleCheck = {
    exists: (value: string) => checked.includes(value),
    toggle: (value: string) => {
      setPending(
        pending.includes(value)
          ? pending.filter(item => item !== value)
          : pending.concat(value)
      );

      const newCheckeds = checked.includes(value)
        ? checked.filter(item => item !== value)
        : [...checked, value];

      setChecked(newCheckeds);
      updatePermissions(newCheckeds);
    },
  };

  React.useEffect(() => {
    if (permissions?.selected) {
      setChecked(permissions.selected);
    }
  }, [permissions]);

  const allPermissionsAvailable = React.useMemo(() => permissionsNames || [], [
    permissionsNames,
  ]);

  const handleCheckAllExists = (values: string[]) =>
    values.reduce(
      (permissionsResponse, currentValue) =>
        permissionsResponse && checked.includes(currentValue),
      true
    );

  const handleToggleAll = (values: string[]) => {
    let newListPending = [...pending];
    values.forEach(value => {
      if (newListPending.includes(value)) {
        newListPending = newListPending.filter(item => item !== value);
      } else {
        newListPending = [...newListPending, value];
      }
    });
    setPending(newListPending);
    if (handleCheckAllExists(values)) {
      setChecked([]);
      updatePermissions([]);
    } else {
      setChecked(values);
      updatePermissions(values);
    }
  };

  return (
    <React.Fragment>
      {permissionList.length === 0 ? (
        <Grid container justify="center" className={classes.warning}>
          <Alert className={classes.alert} severity="warning" color="info">
            <Grid item>
              <Typography>{i18n.t('NO_PERMISSIONS_IN_GROUP_KEY')}</Typography>
            </Grid>
          </Alert>
        </Grid>
      ) : (
        <Grid
          container
          spacing={2}
          className={clsx({
            [classes.disableArea]: permissions?.loading,
          })}
        >
          <Grid item xs={12} style={{ margin: '32px 0 8px' }}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2">{subtitle}</Typography>
          </Grid>
          {(permissions?.loading && !permissionsNames) || isLoading ? (
            <Loading />
          ) : (
            <>
              <Grid container className={classes.superTitle}>
                <Checkbox
                  size="small"
                  checked={handleCheckAllExists(allPermissionsAvailable)}
                  onChange={() => {
                    handleToggleAll(allPermissionsAvailable);
                  }}
                  className={classes.checkIcon}
                />
                <Typography className={classes.titleText}>
                  {i18n.t('GROUPS_PAGE.SELECT_ALL_KEY')}
                </Typography>
              </Grid>
              <PermissionCard
                groupPermissions={permissionList}
                entityKey="operator"
                permissions={permissions}
                pending={pending}
                handleCheck={handleCheck}
                handleCheckAllExists={handleCheckAllExists}
                setPending={setPending}
                updatePermissions={updatePermissions}
                checked={checked}
                setChecked={setChecked}
              />
            </>
          )}
        </Grid>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = ({
  permission: { permissions, isLoading },
}: ApplicationState) => ({
  permissions: permissions[ENTITY_NAME],
  isLoading,
});

export default connect(mapStateToProps)(ListPermissions);
